import AppState from 'AppState';
import React, { useContext, useEffect } from 'react';
import { WithTestIdOptional } from 'shared-client/types/test';
import AutoSuggestContext from '../auto-suggest/AutoSuggestContext';
import AutoSuggestNoResultsText from '../auto-suggest/AutoSuggestNoResultsText';
import { AutoSuggestRenderMenuProps } from '../auto-suggest/Types';
import EditContext from './EditContext';
import AutoSuggestSingleField from './fields/AutoSuggestSingleField';

export enum ScopeFieldInclude {
	All = 0,
	SiteCollectionsOnly = 1,
	SitesOnly = 2,
}

interface ScopeFieldProps extends WithTestIdOptional {
	disabled?: boolean;
	include?: ScopeFieldInclude;
}

interface AppArrayItem {
	label: string;
	value: string;
	type: 'Site' | 'Site Collection';
}

const compare = (a: AppArrayItem, b: AppArrayItem) => {
	if (a.label < b.label) {
		return -1;
	}

	if (a.label > b.label) {
		return 1;
	}

	return 0;
};

const defaultTestId = 'scope-picker';

/**
 * Field that displays the scoping options (Global, Site Collection, Site) for a scoped entity
 * @param param0
 */
function ScopeField({ disabled = false, include = ScopeFieldInclude.All, testId }: ScopeFieldProps) {
	const { setCurrentScope, currentScope, isCreateMode, setOriginalScope, setScopeChanged, originalScope } =
		useContext(EditContext);

	const mergedSitesAndCollections: Array<AppArrayItem> = [];

	useEffect(() => {
		if (!isCreateMode) {
			setOriginalScope(currentScope);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (include === ScopeFieldInclude.All || include === ScopeFieldInclude.SitesOnly) {
		AppState.sites.map((site) => {
			mergedSitesAndCollections.push({ label: site.name, value: site.id, type: 'Site', ...site });
		});
	}

	if (include === ScopeFieldInclude.All || include === ScopeFieldInclude.SiteCollectionsOnly) {
		AppState.siteCollections.map((siteCollection) => {
			mergedSitesAndCollections.push({
				label: siteCollection.name,
				value: siteCollection.id,
				type: 'Site Collection',
				...siteCollection,
			});
		});
	}

	return AppState.config.multiSiteEnabled ? (
		<>
			<span className="visually-hidden">
				Changing this scope field will automatically update the content of various tabs.
			</span>
			<AutoSuggestSingleField
				label="Scope"
				forceEmptyLabel
				labelElementHidden
				name="scope"
				autoSuggestOptions={{ staticData: mergedSitesAndCollections }}
				showPlaceholder={true}
				placeholder="Global"
				disabled={disabled}
				testId={testId ? testId : 'scope-picker'}
				renderMenu={(props) => {
					return <ScopeFieldMenu {...props} />;
				}}
				onChange={(scope) => {
					setScopeChanged((scope && scope.value) !== (originalScope && originalScope.value));
					setCurrentScope(scope);
				}}
			/>
		</>
	) : null;
}

function ScopeFieldMenu(props: AutoSuggestRenderMenuProps) {
	const context = useContext(AutoSuggestContext);

	const {
		data: { results, hasMore, isLoading },
		testId,
	} = props;

	const { downshift } = context;

	if (!downshift) {
		return null;
	}

	const siteResults: AppArrayItem[] = results
		.filter((resultsItem) => {
			return resultsItem.type === 'Site';
		})
		.sort(compare);

	const siteCollectionResults: AppArrayItem[] = results
		.filter((resultsItem) => {
			return resultsItem.type === 'Site Collection';
		})
		.sort(compare);

	const { getMenuProps, getItemProps, inputValue, highlightedIndex } = downshift;

	return (
		<div className="autosuggest-menu-categorized">
			<ul
				className="dropdown-menu autosuggest-menu-categorized__list"
				{...getMenuProps({
					open: true,
				})}
			>
				{siteCollectionResults && siteCollectionResults.length > 0 ? (
					<>
						<li className="autosuggest-menu-categorized__item-group-title">SITE COLLECTION</li>
						{siteCollectionResults.map((item, index) => (
							// tslint:disable-next-line: jsx-key
							<li
								data-testid={`${defaultTestId}-${item.value}`}
								key={index}
								className={
									highlightedIndex === index
										? 'autosuggest-menu-categorized__item autosuggest-menu-categorized__item--highlighted'
										: 'autosuggest-menu-categorized__item'
								}
								{...getItemProps({
									item,
									index: index,
									key: `${item.value}-${index}`,
								})}
							>
								<span>{item.label}</span>
							</li>
						))}
					</>
				) : null}

				{siteResults && siteResults.length > 0 ? (
					<>
						<li className="autosuggest-menu-categorized__item-group-title">SITES</li>
						{siteResults.map((item, index) => (
							// tslint:disable-next-line: jsx-key
							<li
								data-testid={`${testId ? testId : defaultTestId}-${item.value}`}
								key={index}
								className={
									highlightedIndex === index + siteCollectionResults.length
										? 'autosuggest-menu-categorized__item autosuggest-menu-categorized__item--highlighted'
										: 'autosuggest-menu-categorized__item'
								}
								{...getItemProps({
									item,
									index: index + siteCollectionResults.length,
									key: `${item.value}-${index}`,
								})}
							>
								<span>{item.label}</span>
							</li>
						))}
					</>
				) : null}
				{isLoading ? <li className="autosuggest-menu-categorized__item">Loading...</li> : null}
				{!isLoading && hasMore ? (
					<li className="autosuggest-menu-categorized__item-group-title">
						<em>More results available. Enter keyword to search...</em>
					</li>
				) : null}
				{!isLoading && results && results.length === 0 ? (
					<li className="autosuggest-menu-categorized__item">
						<AutoSuggestNoResultsText inputValue={inputValue} />
					</li>
				) : null}
			</ul>
		</div>
	);
}

export default ScopeField;
